import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { X, Snowflake, AlertCircle, CheckCircle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const StreakFreezeModal = ({ isOpen, onClose, onUse, streakFreezeAvailable, streak }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleUse = async () => {
    setLoading(true);
    try {
      await onUse();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md relative">
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2"
          onClick={onClose}
        >
          <X className="w-5 h-5" />
        </Button>

        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-2xl">
            <Snowflake className="w-8 h-8 text-blue-500" />
            {language === 'fr' ? 'Streak Freeze' : 'Streak Freeze'}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {streakFreezeAvailable ? (
            <>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">
                      {language === 'fr' 
                        ? 'Protégez votre série !' 
                        : 'Protect your streak!'
                      }
                    </h3>
                    <p className="text-sm text-blue-800">
                      {language === 'fr'
                        ? `Vous avez un Streak Freeze disponible. Si vous manquez un jour, votre série actuelle de ${streak} jour${streak > 1 ? 's' : ''} sera automatiquement protégée.`
                        : `You have a Streak Freeze available. If you miss a day, your current streak of ${streak} day${streak !== 1 ? 's' : ''} will be automatically protected.`
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-sm">
                  {language === 'fr' ? 'Comment ça marche ?' : 'How does it work?'}
                </h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• {language === 'fr' 
                    ? 'Le Streak Freeze se déclenche automatiquement' 
                    : 'Streak Freeze activates automatically'
                  }</li>
                  <li>• {language === 'fr' 
                    ? 'Utilisable une seule fois, puis doit être regagné' 
                    : 'Can be used once, then must be re-earned'
                  }</li>
                  <li>• {language === 'fr' 
                    ? 'Gagnez-en un nouveau en maintenant une série de 7 jours' 
                    : 'Earn a new one by maintaining a 7-day streak'
                  }</li>
                </ul>
              </div>

              <Button
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                onClick={onClose}
              >
                {language === 'fr' ? 'Compris !' : 'Got it!'}
              </Button>
            </>
          ) : (
            <>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-orange-900 mb-2">
                      {language === 'fr' 
                        ? 'Aucun Streak Freeze disponible' 
                        : 'No Streak Freeze available'
                      }
                    </h3>
                    <p className="text-sm text-orange-800">
                      {language === 'fr'
                        ? 'Vous avez déjà utilisé votre Streak Freeze. Pour en gagner un nouveau, maintenez une série de 7 jours consécutifs.'
                        : 'You already used your Streak Freeze. To earn a new one, maintain a 7-day consecutive streak.'
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center py-4">
                <div className="text-6xl mb-4">❄️</div>
                <p className="text-gray-600 text-sm">
                  {language === 'fr'
                    ? 'Continuez à apprendre chaque jour pour débloquer un nouveau Streak Freeze.'
                    : 'Keep learning every day to unlock a new Streak Freeze.'
                  }
                </p>
              </div>

              <Button
                className="w-full"
                variant="outline"
                onClick={onClose}
              >
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StreakFreezeModal;
